import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { fetchPatternPerformanceBreakdown } from '../../api/adminPatternPerformance';
import {
  BUCKET_TIMEFRAMES,
  DEFAULT_BUCKET_ENGINE_VERSIONS,
  DEFAULT_BUCKET_TIMEFRAMES,
  HISTORICAL_PATTERN_ENGINE_VERSIONS,
  resolvePatternEngineVersionLabel,
} from '../pattern-scanner/scannerExperienceShared';

const PAGE_COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
};

const SCORE_BUCKETS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];

const POP_DETECTIONS = 'détections non ambiguës';
const POP_DEFINITIVE = 'évaluations TP définitives (RESOLVED + INVALIDATED)';

const THRESHOLD_STORAGE_KEY = 'ecotoneai.patternPerformance.tpHighlightThresholds.v1';
const DEFAULT_THRESHOLDS = { tp1: 80, tp2: 60, tp3: 40 };

/**
 * Metric catalogue. BO/Inv. use the detection population; the four TP metrics use the definitive
 * evaluation population (PENDING/UNKNOWN/AMBIGUOUS excluded). TP metrics carry a `thresholdKey` used
 * for the configurable highlight.
 */
const METRIC_DEFS = [
  {
    key: 'breakout',
    short: 'BO',
    full: 'Breakout',
    population: POP_DETECTIONS,
    definition: 'Part des détections non ambiguës ayant cassé le niveau de breakout.',
    color: PAGE_COLORS.ink,
    sortable: true,
  },
  {
    key: 'invalidation',
    short: 'Inv.',
    full: 'Invalidation du pattern',
    population: POP_DETECTIONS,
    definition: "Part des détections invalidées parmi les détections non ambiguës. Une invalidation peut survenir après l'atteinte d'un ou plusieurs objectifs TP.",
    color: PAGE_COLORS.danger,
    sortable: true,
  },
  {
    key: 'invalidationBeforeTp1',
    short: 'Échec TP1',
    full: 'Invalidation avant TP1',
    population: POP_DEFINITIVE,
    definition: "Proportion d'évaluations définitives invalidées avant l'atteinte du premier objectif (échec avant TP1).",
    color: PAGE_COLORS.danger,
    sortable: true,
  },
  {
    key: 'tp1',
    short: 'TP1',
    full: 'TP1 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le premier objectif.',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp1',
    sortable: true,
  },
  {
    key: 'tp2',
    short: 'TP2',
    full: 'TP2 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le deuxième objectif.',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp2',
    sortable: true,
  },
  {
    key: 'tp3',
    short: 'TP3',
    full: 'TP3 atteint',
    population: POP_DEFINITIVE,
    definition: 'Part des évaluations définitives ayant atteint le troisième objectif.',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp3',
    sortable: true,
  },
];

const COLUMN_WIDTHS = {
  pattern: '32%',
  detections: '8%',
  breakout: '8%',
  invalidation: '8%',
  invalidationBeforeTp1: '12%',
  tp1: '8%',
  tp2: '8%',
  tp3: '8%',
  details: '8%',
};

const SORTABLE_COLUMNS = [
  { key: 'pattern', label: 'Pattern / Version' },
  { key: 'detections', label: 'Détections' },
  ...METRIC_DEFS.map((def) => ({ key: def.key, label: `${def.short} — ${def.full}` })),
];

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeRatio(value) {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
}

function formatPercent(value, fallback = '—') {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return fallback;
  }
  return `${(normalized * 100).toFixed(1)}%`;
}

function toCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function compareEngineVersions(left, right) {
  if (left === right) {
    return 0;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right));
}

/**
 * Displays the CANONICAL engine version actually carried by the row. The API returns already-canonical
 * strings ("v4.3", "v5.1"): passing them to the numeric engine-id helper produced "V?" (regression).
 * Accepts both a canonical string and a raw technical id.
 */
function formatPatternVersion(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return resolvePatternEngineVersionLabel(numeric).toUpperCase();
  }
  return String(value).trim().toUpperCase();
}

function versionRank(value) {
  const match = /v?(\d+)(?:\.(\d+))?/i.exec(String(value || ''));
  if (!match) {
    return [999, 999];
  }
  return [Number(match[1]), match[2] ? Number(match[2]) : 0];
}

function compareCanonicalVersion(left, right) {
  const [leftMajor, leftMinor] = versionRank(left);
  const [rightMajor, rightMinor] = versionRank(right);
  return (leftMajor - rightMajor) || (leftMinor - rightMinor);
}

function loadThresholds() {
  try {
    const raw = window.localStorage ? window.localStorage.getItem(THRESHOLD_STORAGE_KEY) : null;
    if (!raw) {
      return DEFAULT_THRESHOLDS;
    }
    const parsed = JSON.parse(raw);
    return {
      tp1: clampThreshold(parsed.tp1, DEFAULT_THRESHOLDS.tp1),
      tp2: clampThreshold(parsed.tp2, DEFAULT_THRESHOLDS.tp2),
      tp3: clampThreshold(parsed.tp3, DEFAULT_THRESHOLDS.tp3),
    };
  } catch (error) {
    return DEFAULT_THRESHOLDS;
  }
}

function clampThreshold(value, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(100, Math.max(0, numeric));
}

function persistThresholds(thresholds) {
  try {
    if (window.localStorage) {
      window.localStorage.setItem(THRESHOLD_STORAGE_KEY, JSON.stringify(thresholds));
    }
  } catch (error) {
    // storage unavailable: keep in-memory only
  }
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 20,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function ProvisionalMarker() {
  return (
    <Tooltip title="Échantillon partiel : certains taux (TP1/TP2/TP3, échec TP1) sont provisoires tant que le backfill historique n'est pas complet.">
      <Box
        component="span"
        tabIndex={0}
        aria-label="Taux provisoires (échantillon partiel)"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: PAGE_COLORS.warning,
          display: 'inline-block',
          flex: '0 0 auto',
        }}
      />
    </Tooltip>
  );
}

function isProvisional(takeProfits) {
  return Boolean(takeProfits)
    && takeProfits.coverageComplete !== true
    && toCount(takeProfits.definitiveCount) > 0;
}

function coverageLabel(takeProfits, detections) {
  return `TP définitifs : ${toCount(takeProfits?.definitiveCount)} / ${toCount(detections)}`;
}

function rawMetricRate(row, key) {
  const tp = row.takeProfits;
  switch (key) {
    case 'breakout': return toNumber(row.breakoutRate);
    case 'invalidation': return toNumber(row.invalidationRate);
    case 'invalidationBeforeTp1': return toNumber(tp?.invalidationBeforeTp1Rate);
    case 'tp1': return toNumber(tp?.tp1Rate);
    case 'tp2': return toNumber(tp?.tp2Rate);
    case 'tp3': return toNumber(tp?.tp3Rate);
    default: return null;
  }
}

function buildMetrics(row, thresholds) {
  const tp = row.takeProfits;
  const definitive = tp ? toCount(tp.definitiveCount) : 0;
  const counts = {
    breakout: [toNumber(row.breakoutCount), null],
    invalidation: [toNumber(row.invalidationCount), null],
    invalidationBeforeTp1: [tp?.invalidationBeforeTp1Count, definitive],
    tp1: [tp?.tp1Count, definitive],
    tp2: [tp?.tp2Count, definitive],
    tp3: [tp?.tp3Count, definitive],
  };
  return METRIC_DEFS.map((def) => {
    const rate = rawMetricRate(row, def.key);
    const [numerator, denominator] = counts[def.key];
    const hasDenominator = denominator !== null && denominator !== undefined;
    const available = rate !== null && rate !== undefined && (!hasDenominator || toCount(denominator) > 0);
    const threshold = def.thresholdKey ? thresholds[def.thresholdKey] : null;
    const highlighted = Boolean(def.thresholdKey)
      && available
      && normalizeRatio(rate) !== null
      && (normalizeRatio(rate) * 100) >= threshold;
    return {
      ...def,
      rate,
      numerator,
      denominator,
      available,
      highlighted,
      text: available ? formatPercent(rate) : '—',
      fraction: hasDenominator && toCount(denominator) > 0
        ? `${toCount(numerator)} / ${toCount(denominator)} évalués`
        : (numerator !== null && numerator !== undefined ? `${toCount(numerator)} détection(s)` : null),
    };
  });
}

function getSortValue(row, key) {
  switch (key) {
    case 'pattern': return String(row.pattern || '');
    case 'detections': return toNumber(row.detections);
    default: return rawMetricRate(row, key);
  }
}

function compareRows(left, right, key, direction) {
  const factor = direction === 'desc' ? -1 : 1;
  const leftValue = getSortValue(left, key);
  const rightValue = getSortValue(right, key);
  const leftEmpty = leftValue === null || leftValue === undefined;
  const rightEmpty = rightValue === null || rightValue === undefined;
  if (leftEmpty && rightEmpty) {
    return tieBreak(left, right);
  }
  if (leftEmpty) {
    return 1; // nulls always last, both directions
  }
  if (rightEmpty) {
    return -1;
  }
  let comparison;
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    comparison = leftValue - rightValue;
  } else {
    comparison = String(leftValue).localeCompare(String(rightValue));
  }
  if (comparison !== 0) {
    return comparison * factor;
  }
  return tieBreak(left, right);
}

function tieBreak(left, right) {
  const patternComparison = String(left.pattern || '').localeCompare(String(right.pattern || ''));
  if (patternComparison !== 0) {
    return patternComparison;
  }
  return compareCanonicalVersion(left.engineVersion, right.engineVersion);
}

function MetricTooltip({ metric }) {
  return (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>{metric.full}</Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>{`Population : ${metric.population}.`}</Typography>
      {metric.definition ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{metric.definition}</Typography>
      ) : null}
      {metric.fraction ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>{metric.fraction}</Typography>
      ) : null}
    </Box>
  );
}

function MetricValue({ metric }) {
  return (
    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
      <Box
        component="span"
        tabIndex={0}
        data-highlighted={metric.highlighted ? 'true' : 'false'}
        aria-label={`${metric.full} : ${metric.available ? metric.text : 'non disponible'}`}
        sx={{
          cursor: 'help',
          fontWeight: metric.highlighted ? 700 : 400,
          fontSize: 14,
          whiteSpace: 'nowrap',
          color: metric.available ? metric.color : PAGE_COLORS.muted,
          outline: 'none',
          '&:focus-visible': { outline: `2px solid ${alpha(PAGE_COLORS.ink, 0.5)}`, borderRadius: 0.5 },
        }}
      >
        {metric.available ? metric.text : '—'}
      </Box>
    </Tooltip>
  );
}

function PatternCell({ row, showVersion, provisional, threshold }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          title={row.pattern}
          sx={{ fontWeight: 700, color: PAGE_COLORS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {row.pattern}
        </Typography>
        {showVersion ? (
          <Chip
            size="small"
            label={formatPatternVersion(row.engineVersion)}
            sx={{ height: 20, bgcolor: alpha(PAGE_COLORS.ink, 0.06), color: PAGE_COLORS.muted, fontWeight: 800 }}
          />
        ) : null}
        {provisional ? <ProvisionalMarker /> : null}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.1 }}>
          {coverageLabel(row.takeProfits, row.detections)}
        </Typography>
        <LowSampleChip lowSample={row.lowSample} threshold={threshold} />
      </Stack>
    </Box>
  );
}

function RowDetailsContent({ row, thresholds }) {
  const metrics = buildMetrics(row, thresholds);
  return (
    <Box sx={{ p: 2, maxWidth: 420 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
          {row.pattern || 'Pattern'}
        </Typography>
        <Chip size="small" label={formatPatternVersion(row.engineVersion)} sx={{ height: 20 }} />
      </Stack>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
        {`${coverageLabel(row.takeProfits, row.detections)} · Détections : ${toCount(row.detections)}`}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.75}>
        {metrics.map((metric) => (
          <Stack key={metric.key} direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{metric.full}</Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.population}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: metric.highlighted ? 700 : 400, color: metric.available ? metric.color : PAGE_COLORS.muted }}
              >
                {metric.available ? metric.text : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.fraction || '—'}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: PAGE_COLORS.muted }}>
        BO / Inv. : détections non ambiguës. Échec TP1 / TP1 / TP2 / TP3 : évaluations TP définitives.
        Les évaluations PENDING n&apos;entrent pas dans le dénominateur définitif.
      </Typography>
    </Box>
  );
}

function SortControl({ sort, onSort }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Select
        size="small"
        value={sort.key || 'pattern'}
        onChange={(event) => onSort(event.target.value)}
        inputProps={{ 'aria-label': 'Trier par' }}
        sx={{ minWidth: 180, bgcolor: '#fff' }}
      >
        {SORTABLE_COLUMNS.map((column) => (
          <MenuItem key={column.key} value={column.key}>{column.label}</MenuItem>
        ))}
      </Select>
      <Button
        size="small"
        variant="outlined"
        aria-label={sort.direction === 'asc' ? 'Tri croissant' : 'Tri décroissant'}
        onClick={() => onSort(sort.key, sort.direction === 'asc' ? 'desc' : 'asc')}
        sx={{ minWidth: 72 }}
      >
        {sort.direction === 'asc' ? '↑ Croissant' : '↓ Décroissant'}
      </Button>
    </Stack>
  );
}

function ThresholdsPanel({ thresholds, onChange, onReset }) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}`,
        bgcolor: alpha(PAGE_COLORS.ink, 0.02),
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 800, color: PAGE_COLORS.ink, display: 'block', mb: 0.75 }}>
        Seuils de mise en évidence (TP en gras)
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
        {['tp1', 'tp2', 'tp3'].map((key) => (
          <TextField
            key={key}
            label={`${key.toUpperCase()} (%)`}
            type="number"
            size="small"
            value={thresholds[key]}
            onChange={(event) => onChange(key, event.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.5, 'aria-label': `Seuil ${key.toUpperCase()}` }}
            sx={{ width: { xs: '100%', sm: 120 }, bgcolor: '#fff' }}
          />
        ))}
        <Button size="small" onClick={onReset}>Réinitialiser</Button>
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PAGE_COLORS.muted }}>
        Une cellule TP est affichée en gras si son taux (valeur brute, non arrondie) est supérieur ou égal au seuil.
        Les cellules sans évaluation définitive ne sont jamais mises en gras. Ces seuils n&apos;affectent ni le calcul
        ni le tri.
      </Typography>
    </Box>
  );
}

function ConfigurablePatternPerformanceTable({ appliedFilters = {}, lowSampleThreshold = 30 }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selectedVersions, setSelectedVersions] = useState(DEFAULT_BUCKET_ENGINE_VERSIONS);
  const [selectedTimeframes, setSelectedTimeframes] = useState(DEFAULT_BUCKET_TIMEFRAMES);
  const [selectedScoreBuckets, setSelectedScoreBuckets] = useState([]);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState({ anchorEl: null, row: null });
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [thresholds, setThresholds] = useState(loadThresholds);

  const orderedVersions = [...HISTORICAL_PATTERN_ENGINE_VERSIONS].sort(compareEngineVersions);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requestFilters = { ...appliedFilters };
      // Les sélections locales sont l'autorité pour ce breakdown : le filtre
      // global ne doit pas les neutraliser.
      delete requestFilters.version;
      delete requestFilters.timeframe;
      const payload = await fetchPatternPerformanceBreakdown({
        ...requestFilters,
        // Pattern is the mandatory grouping dimension for this screen.
        groupByPattern: true,
        engineVersions: selectedVersions.join(','),
        timeframes: selectedTimeframes.join(','),
        scoreBuckets: selectedScoreBuckets.join(','),
      });
      setRows(payload?.rows || []);
    } catch (loadError) {
      setRows(null);
      setError(loadError.message || 'Impossible de charger la segmentation par bucket de score.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, selectedVersions, selectedTimeframes, selectedScoreBuckets]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(() => {
    if (!rows?.length || !sort.key) {
      return rows || [];
    }
    // Sort the WHOLE loaded cohort (not only visible rows) with a stable tie-break.
    return [...rows].sort((left, right) => compareRows(left, right, sort.key, sort.direction));
  }, [rows, sort]);

  const toggleVersion = (version) => (event) => {
    const checked = event.target.checked;
    setSelectedVersions((current) => (
      checked ? [...current, version] : current.filter((item) => item !== version)
    ));
  };

  const toggleTimeframe = (timeframe) => (event) => {
    const checked = event.target.checked;
    setSelectedTimeframes((current) => (
      checked ? [...current, timeframe] : current.filter((item) => item !== timeframe)
    ));
  };

  const toggleScoreBucket = (bucket) => (event) => {
    const checked = event.target.checked;
    setSelectedScoreBuckets((current) => (
      checked ? [...current, bucket] : current.filter((item) => item !== bucket)
    ));
  };

  const handleSort = (key, forcedDirection) => {
    setSort((current) => {
      if (forcedDirection) {
        return { key, direction: forcedDirection };
      }
      if (current.key !== key) {
        return { key, direction: key === 'pattern' ? 'asc' : 'desc' };
      }
      return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const updateThreshold = (key, value) => {
    if (value === '' || value === null || value === undefined) {
      return;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return;
    }
    const next = { ...thresholds, [key]: Math.min(100, Math.max(0, numeric)) };
    setThresholds(next);
    persistThresholds(next);
  };

  const resetThresholds = () => {
    setThresholds(DEFAULT_THRESHOLDS);
    persistThresholds(DEFAULT_THRESHOLDS);
  };

  const showVersionColumn = selectedVersions.length > 0;
  const openDetails = (event, row) => setDetails({ anchorEl: event.currentTarget, row });
  const closeDetails = () => setDetails({ anchorEl: null, row: null });

  const sortLabel = (key, label, align) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : 'asc'}
      onClick={() => handleSort(key)}
      aria-label={`Trier par ${label}`}
      sx={{ '& .MuiTableSortLabel-icon': { opacity: sort.key === key ? 1 : 0.3 } }}
    >
      {label}
    </TableSortLabel>
  );

  const renderDesktopTable = (list) => (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 800, width: COLUMN_WIDTHS.pattern }}>
            {sortLabel('pattern', showVersionColumn ? 'Pattern / Version' : 'Pattern')}
          </TableCell>
          <TableCell align="center" sx={{ fontWeight: 800, width: COLUMN_WIDTHS.detections }}>
            {sortLabel('detections', 'Détections')}
          </TableCell>
          {METRIC_DEFS.map((def) => (
            <TableCell key={def.key} align="center" sx={{ fontWeight: 800, width: COLUMN_WIDTHS[def.key] }}>
              <Tooltip title={<MetricTooltip metric={def} />}>
                {sortLabel(def.key, def.short)}
              </Tooltip>
            </TableCell>
          ))}
          <TableCell align="center" sx={{ width: COLUMN_WIDTHS.details }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {list.map((row, index) => {
          const metrics = buildMetrics(row, thresholds);
          const provisional = isProvisional(row.takeProfits);
          return (
            <TableRow
              key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
              hover
            >
              <TableCell sx={{ py: 0.75 }}>
                <PatternCell row={row} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
              </TableCell>
              <TableCell align="center" sx={{ px: 0.5, py: 0.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 400 }}>{toCount(row.detections)}</Typography>
              </TableCell>
              {metrics.map((metric) => (
                <TableCell key={metric.key} align="center" sx={{ px: 0.5, py: 0.75 }}>
                  <MetricValue metric={metric} />
                </TableCell>
              ))}
              <TableCell align="center" sx={{ px: 0.25, py: 0.5 }}>
                <Tooltip title={<span>Détails : taux, fractions et dénominateurs</span>}>
                  <IconButton
                    size="small"
                    aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                    onClick={(event) => openDetails(event, row)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const renderMobileCards = (list) => (
    <Stack spacing={1.25}>
      {list.map((row, index) => {
        const metrics = buildMetrics(row, thresholds);
        const provisional = isProvisional(row.takeProfits);
        return (
          <Card
            key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
            variant="outlined"
            sx={{ borderRadius: 3 }}
          >
            <Box sx={{ p: 1.25 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <PatternCell row={row} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                  onClick={(event) => openDetails(event, row)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  mt: 1.25,
                }}
              >
                {metrics.map((metric) => (
                  <Box key={metric.key} sx={{ minWidth: 0 }}>
                    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
                      <Box component="span" tabIndex={0} data-highlighted={metric.highlighted ? 'true' : 'false'} sx={{ cursor: 'help', outline: 'none' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted, fontWeight: 800 }}>
                          {metric.short}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: metric.highlighted ? 700 : 400, color: metric.available ? metric.color : PAGE_COLORS.muted }}
                        >
                          {metric.available ? metric.text : '—'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        );
      })}
    </Stack>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: PAGE_COLORS.ink }} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!rows?.length) {
      return <Alert severity="info">Aucune donnée pour cette sélection.</Alert>;
    }

    return (
      <Box sx={{ width: '100%' }}>
        {isDesktop
          ? <TableContainer sx={{ width: '100%' }}>{renderDesktopTable(sortedRows)}</TableContainer>
          : renderMobileCards(sortedRows)}
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PAGE_COLORS.muted }}>
          BO et Inv. (invalidation du pattern) sont calculés sur les détections non ambiguës. Échec TP1,
          TP1, TP2 et TP3 sont calculés sur les évaluations TP définitives ; les évaluations PENDING,
          UNKNOWN et AMBIGUOUS en sont exclues. Les taux peuvent être provisoires pendant le backfill
          historique.
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
              Analyse de performance par Score
            </Typography>
            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
              Segmentation par pattern et version moteur, croisée avec le bucket de score, filtrable par moteur, timeframe et score.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: alpha(PAGE_COLORS.ink, 0.03),
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Versions moteur
            </Typography>
            <FormGroup row>
              {orderedVersions.map((version) => (
                <FormControlLabel
                  key={version}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedVersions.includes(version)}
                      onChange={toggleVersion(version)}
                    />
                  )}
                  label={formatPatternVersion(version)}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Timeframes
            </Typography>
            <FormGroup row>
              {BUCKET_TIMEFRAMES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedTimeframes.includes(option.value)}
                      onChange={toggleTimeframe(option.value)}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Scores
            </Typography>
            <FormGroup row>
              {SCORE_BUCKETS.map((bucket) => (
                <FormControlLabel
                  key={bucket}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedScoreBuckets.includes(bucket)}
                      onChange={toggleScoreBucket(bucket)}
                    />
                  )}
                  label={bucket}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25} sx={{ flex: 1, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Tri
            </Typography>
            <SortControl sort={sort} onSort={handleSort} />
          </Stack>
        </Stack>

        <ThresholdsPanel thresholds={thresholds} onChange={updateThreshold} onReset={resetThresholds} />

        {renderBody()}

        <Popover
          open={Boolean(details.anchorEl)}
          anchorEl={details.anchorEl}
          onClose={closeDetails}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {details.row ? <RowDetailsContent row={details.row} thresholds={thresholds} /> : null}
        </Popover>
      </Stack>
    </Paper>
  );
}

export default ConfigurablePatternPerformanceTable;
